'use client';

import { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import {
  useGetFieldDefinitionsQuery,
  useGetObjectDefinitionsListQuery,
} from 'src/api/redux/medrm';
import { useCRM } from 'src/context/crm';
import { buckets } from 'src/components/MedRM/constants';
import { Checkbox, PulseIconButton } from 'src/components/PulseUI';
import GridStatusFilterMenuGroup from './GridStatusFilterMenuGroup';
import GridStatusFilterMenuHeader from './GridStatusFilterMenuHeader';

interface GridStatusFilterMenuProps {
  table?: string;
}

/**
 * @description GridStatusFilterMenu component handles Status filtering on the grid
 * @returns React.Node
 */
const GridStatusFilterMenu = ({ table = '' }: GridStatusFilterMenuProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMounted, setIsMounted] = useState(false);

  const { statusFilterOptions, setStatusFilterOptions, setSelectedBuckets } =
    useCRM();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const { data: objectDefinitions, isLoading: isLoadingObjects } =
    useGetObjectDefinitionsListQuery();

  const { data: fieldDefinitions, isLoading: isLoadingFields } =
    useGetFieldDefinitionsQuery(
      {
        objectId: objectDefinitions?.results?.[0]?.id,
      },
      {
        skip: !objectDefinitions?.results?.[0]?.id,
      }
    );

  const statusField = useMemo(() => {
    return fieldDefinitions?.results?.find(
      (field) => field?.label?.toLowerCase() === 'status'
    );
  }, [fieldDefinitions]);

  const groupedOptions = useMemo(() => {
    const options = statusField?.options;

    // Group options by bucket
    const grouped = options?.reduce((acc, option) => {
      if (!acc[option.bucket]) {
        acc[option.bucket] = [];
      }

      acc[option.bucket].push(option);
      return acc;
    }, {});

    // Add order to each option
    if (grouped) {
      for (const bucket in grouped) {
        grouped[bucket].forEach((option, index) => {
          option.order = index; // Adding order, starting from 1
        });
      }
    }

    return grouped;
  }, [statusField]);

  // Clear all selected options
  const handleClearSelected = () => {
    setStatusFilterOptions([]);
    setSelectedBuckets([]);
  };

  const handleAddNoStatus = () => {
    // if it's in the list, remove it
    if (statusFilterOptions?.includes('no_status')) {
      const newStatusFilterOptions = statusFilterOptions?.filter(
        (status) => status !== 'no_status'
      );
      setStatusFilterOptions(newStatusFilterOptions);
    } else {
      setStatusFilterOptions([...statusFilterOptions, 'no_status']);
    }
  };

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 224, // 14rem = 224px, align right edge
      });
    }
  };

  const handleClick = () => {
    if (!isOpen) {
      updateMenuPosition();
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useClickOutside([menuRef, triggerRef], handleClose, isOpen);

  // Update position on scroll/resize when menu is open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [isOpen]);

  if (isLoadingObjects || isLoadingFields) return null;

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      id={`${table}-status-filter-menu`}
      data-testid={`${table}-status-filter-menu`}
      className="fixed z-[9999] w-56 bg-white rounded-lg shadow-lg border border-neutral-200 overflow-auto max-h-80"
      style={{ top: menuPosition.top, left: menuPosition.left }}
      role="menu"
      aria-label="Status filter options"
    >
      <ul className="list-none p-0 m-0 text-sm">
        <li
          id={`${table}-status-filter-menu-item-no-status`}
          data-testid={`${table}-status-filter-menu-item-no-status`}
          className="flex items-center cursor-pointer py-1 px-2"
          onClick={handleAddNoStatus}
          role="menuitemcheckbox"
          aria-checked={statusFilterOptions?.includes('no_status')}
        >
          <Checkbox
            size="sm"
            checked={statusFilterOptions?.includes('no_status')}
            onChange={handleAddNoStatus}
            aria-label="Filter by no status"
          />
          <GridStatusFilterMenuHeader bucket={'no_status'} />
        </li>
        {groupedOptions &&
          buckets?.map((bucket, index) => {
            return (
              <GridStatusFilterMenuGroup
                key={index}
                bucket={bucket}
                groupedOptions={groupedOptions}
                statusField={statusField}
              />
            );
          })}
        <button
          id={`${table}-status-filter-menu-item-clear-selected`}
          data-testid={`${table}-status-filter-menu-item-clear-selected`}
          className="w-full text-left text-sm font-medium border-t border-neutral-300 mt-2 py-2 px-3 hover:bg-neutral-100 transition-colors"
          onClick={handleClearSelected}
          aria-label="Clear all status filters"
        >
          Clear Selected
        </button>
      </ul>
    </div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        id={`${table}-status-filter-menu-icon-button`}
        data-testid={`${table}-status-filter-menu-icon-button`}
      >
        <PulseIconButton
          variant="soft"
          color="neutral"
          icon={faFilter}
          onClick={handleClick}
          aria-label="Filter by status"
          aria-haspopup={true}
          aria-expanded={isOpen}
          aria-controls={`${table}-status-filter-menu`}
          className="font-black"
          tooltip="Filter by status"
        />
      </div>
      {isMounted && createPortal(dropdownMenu, document.body)}
    </>
  );
};

export default GridStatusFilterMenu;
